<template>
  <div>
    <div class="bg-blue-light p-2">
      <DateQuerySetter
        :inEnoughSpace="false"
        btnTitle="Go"
        @onClickGo="onClickGo"
      />
    </div>

    <div class="card mt-1 p-2">
      <div class="table-responsive">
        <table class="table table-bordered">
          <thead>
            <tr>
              <th>SL</th>
              <th>Bulk Reference ID</th>
              <th>Execution Date</th>
              <th class="text-end">Payment Count</th>
              <th class="text-end">Total Amount</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="! rows.length">
              <td colspan="6" class="text-center">No BEFTN statement found.</td>
            </tr>
            <tr v-for="(item, i) in rows" :key="item.reference_no">
              <td>{{ serialNo(i) }}</td>
              <td>{{ item.reference_no }}</td>
              <td>{{ item.execution_date }}</td>
              <td class="text-end">{{ item.payment_count }}</td>
              <td class="text-end">{{ commaFormat(item.total_amount) }}</td>
              <td>
                <button class="btn btn-success btn-sm" @click="onClickExport(item)">
                  <i class="fa fa-file-excel"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <Pagination
        :offset="offset"
        @onPageChange="onPageChange"
        ref="paginationRef"
      />
    </div>

    <Loader v-if="loading" />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, inject } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import Loader from '@/components/atom/LoaderComponent'
import DateQuerySetter from '@/components/atom/DateQuerySetter'
import Pagination from '@/components/atom/Pagination'
import handlePaymentRequisition from '@/services/modules/paymentRequisition'
import figureFormatter from '@/services/utils/figureFormatter'

const showError = inject('showError')
const store = useStore()
const route = useRoute()
const router = useRouter()

const { fetchBeftnBatchList } = handlePaymentRequisition()
const { commaFormat } = figureFormatter()

const loading = ref(false)
const rows = ref([])
const offset = ref(50)
const paginationRef = ref(null)

const companyId = computed(() => route.params.companyId)
const start = computed(() => route.query.start)
const end = computed(() => route.query.end)
const page = computed(() => route.query.page)

const getQuery = () => {
  let query = '?company_id=' + companyId.value
  if (start.value) query += '&start_date=' + start.value
  if (end.value) query += '&end_date=' + end.value
  if (page.value) query += '&page=' + page.value
  query += '&offset=' + offset.value
  return query
}

const serialNo = (index) => {
  const currentPage = Number(page.value) || 1
  return ((currentPage - 1) * offset.value) + index + 1
}

const setPagination = (data) => {
  if (! data) return
  store.commit('setPaginationData', {
    records: data.total,
    perPage: data.per_page,
    currentPage: data.current_page,
  })
}

const fetchList = async () => {
  loading.value = true
  rows.value = []
  try {
    const res = await fetchBeftnBatchList(getQuery())
    if (! res.status) {
      store.commit('resetPagination')
      return
    }
    rows.value = res.data.data
    setPagination(res.data)
  } catch (err) {
    showError('Something is wrong. Check your connectivity!!')
  } finally {
    loading.value = false
  }
}

const onClickExport = (item) => {
  const token = localStorage.getItem('token')
  const url = `${process.env.VUE_APP_BASE_URL}/export/payment-requisition/beftn-statement?company_id=${companyId.value}&reference_no=${encodeURIComponent(item.reference_no)}&_token=${token}`
  const a = document.createElement('a')
  a.setAttribute('href', url)
  a.click()
}

const onClickGo = async () => {
  store.commit('setPaginationData', {})
  const routeQuery = Object.assign({}, route.query)
  routeQuery.page = 1
  await router.push({ path: route.path, query: routeQuery })
  await fetchList()
}

const onPageChange = async (p) => {
  const routeQuery = Object.assign({}, route.query)
  routeQuery.page = p
  await router.push({ path: route.path, query: routeQuery })
  await fetchList()
}

const resetRouteQuery = async () => {
  const routeQuery = Object.assign({}, route.query)
  delete routeQuery.start
  delete routeQuery.end
  routeQuery.page = 1
  await router.push({ path: route.path, query: routeQuery }).catch(function () {})
}

onMounted(async () => {
  store.commit('resetPagination')
  await resetRouteQuery()
  fetchList()
})
</script>
